import { useState } from "react"
import { useAuthStore } from "@/store/auth.store"
import { useGetOrderById } from "@/hooks/buyer/useGetOrderById"
import { useNavigate } from "@tanstack/react-router"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, QuotationDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { QUANTITY_UNITS } from "@/components/dashboard/seller/pages/rfqs/RfqDetailsPage"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { CommercialDocumentCard } from "@/components/dashboard/shared/CommercialDocumentCard"
import { getBuyerInvoiceUrl, getSellerInvoiceUrl, getPurchaseOrderUrl, getPaymentReceiptUrl } from "@/lib/api/order.api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { useGetPayments } from "@/hooks/admin/useGetPayments"
import { useAddPayment } from "@/hooks/admin/useAddPayment"
import { useUpdatePaymentStatus } from "@/hooks/admin/useUpdatePaymentStatus"
import { useUpdateInspectionStatus } from "@/hooks/admin/useUpdateInspectionStatus"
import { useUpdateOrderStatus } from "@/hooks/admin/useUpdateOrderStatus"
import { useAddShipment } from "@/hooks/admin/useAddShipment"
import { useGetTrackingDetails } from "@/hooks/useGetTrackingDetails"
import TrackingDetailsDisplay from "@/components/dashboard/admin/components/TrackingDetailsDisplay"
import {
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  FileText,
  Calendar,
  Package,
  Users,
  Activity,
  CheckCircle2,
  Clock,
  Play,
  DollarSign,
  Link2,
  Plus,
  ShieldCheck,
  Truck,
  Zap,
  Plane,
  Ship,
  ArrowRight,
  ExternalLink,
  Check,
  Building2,
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function OrderDetailView({ order, onBack }: { order: any; onBack: () => void }) {
  const navigate = useNavigate()
  const q = order.quotation
  const charges = q?.charges || []
  const accessToken = useAuthStore((s) => s.accessToken)

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [selectedQuotation, setSelectedQuotation] = useState<any | null>(null)

  const [activeTab, setActiveTab] = useState<"overview" | "commercial" | "production" | "shipment" | "payments" | "inspections">("overview")

  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentType, setPaymentType] = useState<"BUYER_TO_ADMIN" | "ADMIN_TO_SELLER">("BUYER_TO_ADMIN")
  const [paymentAmount, setPaymentAmount] = useState<number>(0)
  const [paymentReference, setPaymentReference] = useState<string>("")

  const [editingInspectionId, setEditingInspectionId] = useState<string | null>(null)
  const [editInspectionStatus, setEditInspectionStatus] = useState<"PENDING" | "IN_PROGRESS" | "COMPLETED" | "REJECTED">("PENDING")
  const [editInspectionNotes, setEditInspectionNotes] = useState("")
  const [editInspectionDocUrl, setEditInspectionDocUrl] = useState("")

  const { data: paymentsData } = useGetPayments(order.id)
  const payments = paymentsData || []
  const addPaymentMutation = useAddPayment(order.id)
  const updatePaymentStatusMutation = useUpdatePaymentStatus(order.id)
  const updateInspectionStatusMutation = useUpdateInspectionStatus(order.id)
  const updateOrderStatusMutation = useUpdateOrderStatus()

  const [showShipmentForm, setShowShipmentForm] = useState(false)
  const [shipmentProvider, setShipmentProvider] = useState("ZIPAWORLD")
  const [shipmentTrackingUrl, setShipmentTrackingUrl] = useState("")
  const [shipmentTrackingId, setShipmentTrackingId] = useState("")

  const addShipmentMutation = useAddShipment(order.id)
  
  const { data: trackingDetails, isLoading: isTrackingLoading } = useGetTrackingDetails(
    order.shipment?.trackingUrl,
    order.shipment?.trackingId
  )

  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!paymentAmount || !paymentReference.trim()) return
    addPaymentMutation.mutate(
      { type: paymentType, amount: paymentAmount, referenceId: paymentReference },
      {
        onSuccess: () => {
          toast.success("Payment added successfully")
          setPaymentAmount(0)
          setPaymentReference("")
          setShowPaymentForm(false)
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to add payment"
          toast.error(message)
        },
      }
    )
  }

  const handleShipmentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!shipmentTrackingId.trim() || !shipmentTrackingUrl.trim()) return
    addShipmentMutation.mutate(
      { provider: shipmentProvider, trackingUrl: shipmentTrackingUrl, trackingId: shipmentTrackingId },
      {
        onSuccess: () => {
          setShipmentTrackingUrl("")
          setShipmentTrackingId("")
          setShipmentProvider("ZIPAWORLD")
          setShowShipmentForm(false)
        },
      }
    )
  }

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto">
      <div className="group relative overflow-hidden bg-white border border-[#be001e]/20 hover:border-[#be001e]/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-sm text-slate-700 font-medium">Back to Orders</span>
          </div>

          <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end gap-2 sm:gap-3 shrink-0">
            <span className={`inline-flex items-center justify-center h-7 gap-1 rounded-lg px-2.5 text-[10px] font-bold border uppercase tracking-wider shrink-0 whitespace-nowrap ${
              order.status === "COMPLETED" || order.status === "CONFIRMED" || order.status === "APPROVED" || order.status === "ACCEPTED"
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : order.status === "PENDING" || order.status === "IN_PRODUCTION" || order.status === "IN_TRANSIT"
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : order.status === "CANCELLED" || order.status === "REJECTED"
                ? "bg-red-50 text-red-700 border-red-100"
                : "bg-slate-50 text-slate-700 border-slate-200"
            }`}>
              STATUS: {order.status}
            </span>
            <select
              value={order.status}
              onChange={(e) => {
                updateOrderStatusMutation.mutate({ orderId: order.id, status: e.target.value as any })
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-7 text-[10px] font-bold uppercase tracking-wider border border-slate-200 rounded-lg px-2.5 bg-white text-slate-700 outline-none focus:border-[#be001e] transition-colors cursor-pointer"
              disabled={updateOrderStatusMutation.isPending}
            >
              <option value="PENDING">PENDING</option>
              <option value="ACCEPTED">ACCEPTED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="IN_PRODUCTION">IN PRODUCTION</option>
              <option value="IN_TRANSIT">IN TRANSIT</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>
        </div>

        <div className="h-px bg-[#be001e]/10 w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <div className="flex overflow-x-auto gap-2 pb-2 border-b border-slate-200 scrollbar-hide">
              {[
                { id: "overview", label: "Overview" },
                { id: "commercial", label: "Invoices" },
                { id: "production", label: "Production" },
                { id: "shipment", label: "Shipment" },
                { id: "payments", label: "Payments" },
                { id: "inspections", label: "Inspections" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-t-lg transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id 
                      ? "text-[#be001e] border-[#be001e] bg-[#be001e]/5" 
                      : "text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="pt-2 space-y-4 min-h-[300px]">
            {activeTab === "commercial" && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* 1. Purchase Orders Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                    <FileText className="size-4 text-[#e6202f]" /> Purchase Orders
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.purchaseOrders && order.purchaseOrders.length > 0 && (
                      order.purchaseOrders.map((po: any) => {
                        const title = po.type === "BUYER_TO_PLATFORM" ? "Buyer Purchase Order" : "Seller Purchase Order"
                        const fileName = po.documentUrl ? po.documentUrl.slice(po.documentUrl.lastIndexOf('/') + 1) : `PurchaseOrder_${po.id.slice(0, 8)}.pdf`
                        const statusType = po.status === "ACCEPTED" ? "success" : po.status === "PENDING" ? "warning" : "error"
                        return (
                          <CommercialDocumentCard
                            key={po.id}
                            title={title}
                            fileName={fileName}
                            status={po.status}
                            statusType={statusType}
                            date={formatDate(po.createdAt)}
                            documentUrl={po.documentUrl}
                            onGetSignedUrl={() => getPurchaseOrderUrl(order.id, po.id, accessToken || "")}
                          />
                        )
                      })
                    )}
                  </div>
                </div>

                {/* 2. Invoices Section */}
                {(order.invoiceUrl || (order.purchaseOrders && order.purchaseOrders.some((po: any) => po.sellerInvoiceUrl))) && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                      <FileText className="size-4 text-[#e6202f]" /> Commercial Invoices
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Buyer Commercial Invoice */}
                      {order.invoiceUrl && (
                        <CommercialDocumentCard
                          title="Buyer Commercial Invoice"
                          fileName={order.invoiceUrl.slice(order.invoiceUrl.lastIndexOf('/') + 1)}
                          status="Issued"
                          statusType="success"
                          date={formatDate(order.createdAt)}
                          documentUrl={order.invoiceUrl}
                          onGetSignedUrl={() => getBuyerInvoiceUrl(order.id, accessToken || "")}
                        />
                      )}

                      {/* Seller Commercial Invoices */}
                      {order.purchaseOrders && order.purchaseOrders
                        .filter((po: any) => po.sellerInvoiceUrl)
                        .map((po: any) => (
                          <CommercialDocumentCard
                            key={`invoice-${po.id}`}
                            title="Seller Commercial Invoice"
                            fileName={po.sellerInvoiceUrl.slice(po.sellerInvoiceUrl.lastIndexOf('/') + 1)}
                            status="Uploaded"
                            statusType="success"
                            date={formatDate(po.updatedAt || po.createdAt)}
                            documentUrl={po.sellerInvoiceUrl}
                            onGetSignedUrl={() => getSellerInvoiceUrl(order.id, accessToken || "")}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "overview" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Users className="size-3.5 text-[#e6202f]" /> Buyer
                  </h4>
                  <div className="group/item flex items-center gap-3 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-[#be001e05] p-3.5 shadow-sm transition-all duration-200">
                    <div className="space-y-0.5 min-w-0">
                      <p className="text-xs text-slate-700 font-semibold tracking-tight font-sans">{order?.buyer?.user?.name}</p>
                    </div>
                  </div>
                </div>

                {order?.company?.legalName && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                      <Building2 className="size-3.5 text-[#e6202f]" /> Company Name
                    </h4>
                    <div 
                      onClick={() => setSelectedCompany(order.company)}
                      className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-slate-700 font-medium hover:text-[#be001e] hover:underline tracking-tight font-sans">
                          {order.company.legalName}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {q ? (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                      <FileText className="size-3.5 text-[#e6202f]" /> Linked Quotation
                    </h4>
                    <div 
                      onClick={() => { if (q.id) setSelectedQuotation(q) }}
                      className={`group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 p-3 shadow-sm transition-all duration-200 ${q.id ? "bg-slate-50/40 hover:bg-slate-50 hover:border-[#be001e]/30 cursor-pointer" : "bg-slate-50/20 opacity-70 cursor-default"}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-extrabold text-[#be001e] hover:underline text-xs tracking-tight font-mono">
                      {q.message}
                    </span>
                      </div>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border font-sans ${
                        q.status === "APPROVED"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                          : q.status === "PENDING"
                          ? "bg-amber-50 text-amber-700 border-amber-100"
                          : q.status === "REJECTED" || q.status === "CLOSED"
                          ? "bg-red-50 text-red-700 border-red-100"
                          : "bg-slate-50 text-slate-700 border-slate-200"
                      }`}>
                        {q.status || "UNKNOWN"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                      <FileText className="size-3.5 text-[#e6202f]" /> Linked Quotation
                    </h4>
                    <div className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 p-3 shadow-sm transition-all duration-200 bg-slate-50/20 opacity-70 cursor-default">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-extrabold text-[#be001e] text-xs tracking-tight font-mono">
                          null
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {(() => {
                  const sellerToAdminQuotation = q?.sellerToAdminQuotation
                  const adminToSellerRfq = sellerToAdminQuotation?.adminToSellerRfq
                  const target = adminToSellerRfq?.targets?.[0]
                  if (!target?.company && !target?.product) return null

                  return (
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Package className="size-3.5 text-[#e6202f]" /> Sourcing Target Item(s)
                      </h4>
                      <TargetItemView
                        target={target}
                        onProductClick={(p) => setSelectedProductId(p.id)}
                        onCompanyClick={(c) => setSelectedCompany(c)}
                        onVariantClick={(v) => setSelectedVariantId(v.id)}
                        showSpecsGrid
                      />
                    </div>
                  )
                })()}

                {/* {order.invoiceUrl && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <FileText className="size-3.5 text-[#e6202f]" /> Invoice
                    </h4>
                    <div className="group/item flex items-center justify-between rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-[#be001e]/[0.02] p-4 text-left shadow-sm transition-all duration-200">
                      <div className="flex items-center gap-3">
                        <div className="space-y-0.5">
                          <h4 className="font-extrabold text-slate-800 text-sm tracking-tight">Official Invoice</h4>
                        </div>
                      </div>
                      <a
                        href={order.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[#be001e] bg-[#be001e]/5 hover:bg-[#be001e] hover:text-white px-3 py-1.5 rounded-lg transition-all duration-300"
                      >
                        View Invoice
                        <Link2 className="size-3" />
                      </a>
                    </div>
                  </div>
                )} */}

                {order.zipaWorldQuotation && (
                  <div className="space-y-3 pt-2">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Zap className="size-3.5 text-amber-500 fill-amber-500/20" /> Selected Logistics Rate (Zipaworld)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(() => {
                        const zq = order.zipaWorldQuotation
                        const isAir = zq.shipmentMode?.toUpperCase() === "AIR"
                        return (
                          <div className="relative overflow-hidden group/zq flex flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-4.5 space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                              <div className="flex items-center gap-2">
                                <div className={`p-2 rounded-lg border ${
                                  isAir 
                                    ? "bg-sky-50 border-sky-100 text-sky-600" 
                                    : "bg-blue-50 border-blue-100 text-blue-600"
                                }`}>
                                  {isAir ? <Plane className="size-4" /> : <Ship className="size-4" />}
                                </div>
                                <div>
                                  <h5 className="font-extrabold text-slate-800 text-[13px] leading-tight">
                                    {zq.serviceName || "Standard"}
                                  </h5>
                                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                    {zq.quotationType?.replace(/_/g, " ") || "LOGISTICS RATE"}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider text-slate-500 bg-slate-100 border-slate-200/50">
                                  {zq.transitTime || "N/A"}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-5 items-center gap-2 text-center py-1">
                              <div className="col-span-2 text-left min-w-0">
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">ORIGIN</span>
                                <span className="font-bold text-slate-700 text-xs truncate block" title={zq.origin}>{zq.origin || "N/A"}</span>
                              </div>
                              <div className="col-span-1 flex flex-col items-center justify-center">
                                <ArrowRight className="size-3 text-slate-400" />
                              </div>
                              <div className="col-span-2 text-right min-w-0">
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">DESTINATION</span>
                                <span className="font-bold text-slate-700 text-xs truncate block" title={zq.destination}>{zq.destination || "N/A"}</span>
                              </div>
                            </div>

                            <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3.5 text-xs font-semibold text-slate-655 space-y-2">
                              <div className="flex justify-between items-center text-[11.5px]">
                                <span className="text-slate-400">Freight Amount:</span>
                                <span className="font-mono text-slate-700">{zq.freightAmount || "N/A"}</span>
                              </div>
                              <div className="flex justify-between items-center text-[11.5px]">
                                <span className="text-slate-400">GST:</span>
                                <span className="font-mono text-slate-700">{zq.gst || "N/A"}</span>
                              </div>
                              <div className="h-px bg-slate-200/60 my-1" />
                              <div className="flex justify-between items-center text-xs font-bold">
                                <span className="text-slate-800">Total Amount:</span>
                                <span className="text-[#e6202f] font-mono font-black">{zq.totalAmount || "N/A"}</span>
                              </div>
                            </div>

                            {zq.viewRates && (
                              <div className="pt-1">
                                <a 
                                  href={zq.viewRates} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="w-full h-9 border border-[#be001e]/20 text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/40 rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold transition-all cursor-pointer shadow-sm"
                                >
                                  <ExternalLink className="size-3.5" /> View Rates Details
                                </a>
                              </div>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                        <Package className="size-3.5 text-[#e6202f]" /> Charges Breakdown
                      </h4>
                    </div>
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3">
                      {!q || !charges || charges.length === 0 ? (
                        <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                      ) : (
                      <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                        <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <tr>
                            <th className="p-3 min-w-[160px]">Description</th>
                            <th className="p-3 text-center w-32">Unit Price</th>
                            <th className="p-3 text-center w-24">Quantity</th>
                            <th className="p-3 text-center w-24">Unit</th>
                            <th className="p-3 text-center w-28">Discount (%)</th>
                            <th className="p-3 text-center w-24">Tax (%)</th>
                            <th className="p-3 text-center w-24">Tax Type</th>
                            <th className="p-3 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {charges.map((charge: any) => {
                              const isAdvanced = charge.unitPrice !== undefined;
                              const desc = charge.description || "N/A"
                              const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                              const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                              const disc = isAdvanced ? Number(charge.discount || 0) : 0
                              const tax = isAdvanced ? Number(charge.tax || 0) : 0
                              
                              const base = price * qty
                              const sub = base - (base * disc / 100)
                              const lineTotal = sub + (sub * tax / 100)
                              const unitRaw = charge.measureUnit || charge.unit || charge.quantityUnit
                              const unitDisplay = isAdvanced && unitRaw ? (QUANTITY_UNITS.find((u) => u.code.toLowerCase() === String(unitRaw).toLowerCase() || u.name.toLowerCase() === String(unitRaw).toLowerCase())?.code || unitRaw) : "-"

                              return (
                                <tr key={charge.id || charge.description} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="p-3 text-slate-800 font-semibold">
                                    {charge.productVariantId ? (
                                      <button
                                        type="button"
                                        onClick={() => setSelectedVariantId(charge.productVariantId)}
                                        className="text-[#be001e] hover:text-[#e6202f] hover:underline text-left cursor-pointer transition-colors"
                                      >
                                        {charge.description || "-"}
                                      </button>
                                    ) : (
                                      charge.description || "-"
                                    )}
                                  </td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced && charge.unitPrice !== null && charge.unitPrice !== undefined && charge.unitPrice !== "" ? charge.unitPrice : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{unitDisplay}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">
                                    {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                                  </td>
                                  <td className="p-3 text-right text-slate-900 font-bold font-mono">
                                    {lineTotal.toFixed(2)}
                                  </td>
                                </tr>
                              )
                            })
                          }
                        </tbody>
                      </table>
                      </div>
                      )}
                    </div>

                    {q && (() => {
                      const computedChargesTotal = charges.reduce((acc: number, c: any) => {
                        const isAdv = c.unitPrice !== undefined
                        const p = isAdv ? Number(c.unitPrice || 0) : Number(c.amount || 0)
                        const qVal = isAdv ? ((c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1) : 1
                        const d = isAdv ? Number(c.discount || 0) : 0
                        const t = isAdv ? Number(c.tax || 0) : 0
                        const b = p * qVal
                        const s = b - (b * d / 100)
                        return acc + s + (s * t / 100)
                      }, 0)
                      const grandTotal = (order.totalAmount !== undefined && order.totalAmount !== null)
                        ? Number(order.totalAmount)
                        : computedChargesTotal

                      return (
                        <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                          <span className="text-[12px] font-bold text-[#be001e] font-mono">
                            {q.currencyCode} {grandTotal.toFixed(2)}
                          </span>
                        </div>
                      )
                    })()}
                  </div>
              </div>
            )}

            {activeTab === "production" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const stages = order.productionStages || []
                  if (stages.length === 0) return <p className="text-xs text-slate-400 italic">No production stages logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Activity className="size-3.5 text-[#e6202f]" /> Production Stages
                      </h4>
                      <div className="space-y-3">
                        {stages.map((stage: any) => (
                          <div
                            key={stage.id}
                            className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 border border-slate-100 rounded-xl p-4 gap-3"
                          >
                            <div className="space-y-1 text-left">
                              <h5 className="text-xs font-bold text-slate-800">{stage.name}</h5>
                              {stage.description && (
                                <p className="text-[11px] text-slate-500 leading-normal">{stage.description}</p>
                              )}
                              <span className="text-[9px] text-slate-400 block">
                                Added: {formatDate(stage.createdAt)}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2 self-end sm:self-auto">
                              <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${
                                stage.status === "COMPLETED"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                  : stage.status === "IN_PROGRESS"
                                  ? "bg-sky-50 text-sky-700 border-sky-100"
                                  : "bg-amber-50 text-amber-700 border-amber-100"
                              }`}>
                                {stage.status === "COMPLETED" && <CheckCircle2 className="size-2.5" />}
                                {stage.status === "IN_PROGRESS" && <Play className="size-2.5 animate-pulse" />}
                                {stage.status === "PENDING" && <Clock className="size-2.5" />}
                                {stage.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {activeTab === "shipment" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3 text-left">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Truck className="size-3.5 text-[#e6202f]" /> Shipment Details
                    </h4>
                    {!order.shipment && (
                      <button
                        onClick={() => setShowShipmentForm(!showShipmentForm)}
                        className="flex items-center gap-1 text-[10px] font-bold text-[#e6202f] hover:text-[#be001e] transition-colors bg-[#e6202f]/5 hover:bg-[#e6202f]/10 border border-[#e6202f]/20 rounded-lg px-2.5 py-1 cursor-pointer"
                      >
                        <Plus className="size-3" /> Add Shipment
                      </button>
                    )}
                  </div>

                  {showShipmentForm && !order.shipment && (
                    <form onSubmit={handleShipmentSubmit} className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Provider</label>
                          <select
                            value={shipmentProvider}
                            onChange={(e) => setShipmentProvider(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#be001e] transition-colors cursor-pointer"
                          >
                            <option value="ZIPAWORLD">ZIPAWORLD</option>
                            <option value="DHL">DHL</option>
                            <option value="FEDEX">FEDEX</option>
                            <option value="UPS">UPS</option>
                          </select>
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tracking URL</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. https://api.trackingprovider.com/track"
                            value={shipmentTrackingUrl}
                            onChange={(e) => setShipmentTrackingUrl(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tracking ID</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. TRK987654321"
                          value={shipmentTrackingId}
                          onChange={(e) => setShipmentTrackingId(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setShowShipmentForm(false)}
                          className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={addShipmentMutation.isPending}
                          className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#e6202f] hover:bg-[#be001e] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {addShipmentMutation.isPending ? "Adding..." : "Add Shipment"}
                        </button>
                      </div>
                    </form>
                  )}

                  {order.shipment ? (
                    <div className="space-y-3 bg-slate-50/40 border border-slate-100 rounded-xl p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs pb-3 border-b border-slate-200/50">
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Carrier Provider</span>
                          <span className="font-extrabold text-slate-800 uppercase">{order.shipment.provider}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Number</span>
                          <span className="font-mono font-bold text-slate-800">{order.shipment.trackingId}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Endpoint</span>
                          <a
                            href={order.shipment.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#e6202f] hover:underline font-mono text-[10.5px] truncate block"
                            title={order.shipment.trackingUrl}
                          >
                            {order.shipment.trackingUrl}
                          </a>
                        </div>
                      </div>

                      {isTrackingLoading ? (
                        <div className="flex items-center justify-center py-6 gap-2 text-xs font-semibold text-slate-500">
                          <Loader2 className="size-4.5 animate-spin text-[#e6202f]" />
                          Loading live tracking updates...
                        </div>
                      ) : (
                        <TrackingDetailsDisplay trackingData={trackingDetails} />
                      )}
                    </div>
                  ) : (
                    !showShipmentForm && (
                      <p className="text-xs text-slate-400 italic">No shipment details added yet.</p>
                    )
                  )}
                </div>
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <DollarSign className="size-3.5 text-[#e6202f]" /> Payments
                    </h4>
                    <button
                      onClick={() => setShowPaymentForm(!showPaymentForm)}
                      className="flex items-center gap-1 text-[10px] font-bold text-[#e6202f] hover:text-[#be001e] transition-colors bg-[#e6202f]/5 hover:bg-[#e6202f]/10 border border-[#e6202f]/20 rounded-lg px-2.5 py-1 cursor-pointer"
                    >
                      <Plus className="size-3" /> Add Payment
                    </button>
                  </div>

                  {showPaymentForm && (
                    <form onSubmit={handlePaymentSubmit} className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Payment Type</label>
                          <select
                            value={paymentType}
                            onChange={(e) => setPaymentType(e.target.value as any)}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#be001e] transition-colors cursor-pointer"
                          >
                            <option value="BUYER_TO_ADMIN">Buyer to Admin</option>
                            <option value="ADMIN_TO_SELLER">Admin to Seller</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Amount</label>
                          <input
                            type="number"
                            required
                            min="0.01"
                            step="any"
                            placeholder="e.g. 7200"
                            value={paymentAmount || ""}
                            onChange={(e) => setPaymentAmount(parseFloat(e.target.value))}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Reference ID</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. TXN123456789"
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setShowPaymentForm(false)}
                          className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={addPaymentMutation.isPending}
                          className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#e6202f] hover:bg-[#be001e] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {addPaymentMutation.isPending ? "Adding..." : "Add Payment"}
                        </button>
                      </div>
                    </form>
                  )}

                  {payments.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No payments logged yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {payments.map((payment: any) => (
                        <div
                          key={payment.id}
                          className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl p-4 gap-3 transition-colors text-xs"
                        >
                          <div className="space-y-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className={`font-bold text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded border ${
                                payment.type === "BUYER_TO_ADMIN"
                                  ? "bg-blue-50 text-blue-700 border-blue-100"
                                  : "bg-indigo-50 text-indigo-700 border-indigo-100"
                              }`}>
                                {payment.type.replace(/_/g, " ")}
                              </span>
                            </div>
                            {payment.referenceId && (
                              <div className="flex items-center gap-1 text-slate-500 font-medium">
                                <span className="font-semibold text-slate-400">Ref ID:</span>
                                <span className="font-mono">{payment.referenceId}</span>
                              </div>
                            )}
                            {payment.referenceUrl && (
                              <div className="flex items-center gap-1 text-[#e6202f]">
                                <Link2 className="size-3" />
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await getPaymentReceiptUrl(order.id, payment.id, accessToken || "")
                                      if (res?.url) {
                                        window.open(res.url, "_blank", "noopener,noreferrer")
                                      }
                                    } catch (error) {
                                      console.error("Failed to get payment receipt URL:", error)
                                      toast.error("Failed to fetch payment receipt document")
                                    }
                                  }}
                                  className="hover:underline font-medium text-left cursor-pointer"
                                >
                                  View Payment Receipt
                                </button>
                              </div>
                            )}
                            <span className="text-[9px] text-slate-400 block">
                              Logged: {formatDate(payment.createdAt)}
                            </span>
                          </div>

                          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3 self-end sm:self-auto">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${
                                payment.status === "COMPLETED"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                  : payment.status === "PENDING"
                                  ? "bg-amber-50 text-amber-700 border-amber-100"
                                  : payment.status === "FAILED"
                                  ? "bg-red-50 text-red-700 border-red-100"
                                  : "bg-slate-50 text-slate-700 border-slate-200"
                              }`}>
                                {payment.status}
                              </span>

                              <select
                                value={payment.status}
                                onChange={(e) =>
                                  updatePaymentStatusMutation.mutate(
                                    { paymentId: payment.id, status: e.target.value as any },
                                    {
                                      onSuccess: () => toast.success("Payment status updated successfully"),
                                      onError: (err: any) => {
                                        toast.error(err?.response?.data?.message || err?.message || "Failed to update payment status")
                                      },
                                    }
                                  )
                                }
                                className="text-[10px] font-bold uppercase tracking-wide border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 outline-none focus:border-[#be001e] transition-colors cursor-pointer"
                                disabled={updatePaymentStatusMutation.isPending}
                              >
                                <option value="PENDING">Pending</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="FAILED">Failed</option>
                              </select>
                            </div>
                            <span className="text-sm font-black text-slate-900 font-mono">
                              {q?.currencyCode ?? "—"} {payment.amount?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "inspections" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const inspections = order.inspections || (order.inspection ? [order.inspection] : [])
                  if (inspections.length === 0) return <p className="text-xs text-slate-400 italic">No inspections logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <ShieldCheck className="size-3.5 text-[#e6202f]" /> Inspections
                      </h4>
                      <div className="grid grid-cols-1 gap-3">
                        {inspections.map((inspection: any) => (
                          <div
                            key={inspection.id}
                            className="group relative flex flex-col bg-white border border-slate-200/80 hover:border-[#be001e]/30 rounded-xl p-3.5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_rgba(190,0,30,0.08)] transition-all duration-300 overflow-hidden text-xs"
                          >
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#be001e] to-[#e6202f] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                            {editingInspectionId === inspection.id ? (
                              <div className="w-full space-y-3 text-left text-xs animate-in fade-in slide-in-from-bottom-2 duration-300 bg-slate-50/50 p-3.5 rounded-lg border border-[#be001e]/20 relative z-10">
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-lg" />
                                <div className="flex justify-between items-center pb-2 border-b border-slate-200/80">
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center justify-center size-6 rounded-md bg-[#be001e]/10 text-[#be001e]">
                                      <ShieldCheck className="size-3.5" />
                                    </div>
                                    <span className="font-extrabold text-slate-800 text-sm tracking-tight">Update Inspection Details</span>
                                  </div>
                                  <span className="font-mono text-[9px] text-slate-500 font-bold uppercase bg-white px-2 py-0.5 rounded shadow-sm border border-slate-100">
                                    ID: {inspection.id.slice(0, 8)}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-0.5">
                                  <div className="space-y-1">
                                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 ml-1">Status</label>
                                    <div className="relative">
                                      <select
                                        value={editInspectionStatus}
                                        onChange={(e) => setEditInspectionStatus(e.target.value as any)}
                                        className="w-full bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#be001e]/20 focus:border-[#be001e] transition-all cursor-pointer appearance-none shadow-sm"
                                      >
                                        <option value="PENDING">Pending</option>
                                        <option value="IN_PROGRESS">In Progress</option>
                                        <option value="COMPLETED">Completed</option>
                                        <option value="REJECTED">Rejected</option>
                                      </select>
                                      <ChevronRight className="size-3 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 rotate-90 pointer-events-none" />
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 ml-1">Document URL</label>
                                    <div className="relative">
                                      <Link2 className="size-3 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                      <input
                                        type="text"
                                        placeholder="https://example.com/report.pdf"
                                        value={editInspectionDocUrl}
                                        onChange={(e) => setEditInspectionDocUrl(e.target.value)}
                                        className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#be001e]/20 focus:border-[#be001e] transition-all shadow-sm"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 ml-1">Inspector Notes / Findings</label>
                                  <textarea
                                    placeholder="Detail the findings from the inspection..."
                                    value={editInspectionNotes}
                                    onChange={(e) => setEditInspectionNotes(e.target.value)}
                                    className="w-full min-h-[60px] bg-white border border-slate-200 rounded-lg p-2.5 text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#be001e]/20 focus:border-[#be001e] transition-all shadow-sm resize-y"
                                  />
                                </div>

                                <div className="flex justify-end gap-2 pt-2">
                                  <button
                                    type="button"
                                    onClick={() => setEditingInspectionId(null)}
                                    className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 px-3 py-1.5 rounded-lg transition-all cursor-pointer"
                                    disabled={updateInspectionStatusMutation.isPending}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      updateInspectionStatusMutation.mutate({
                                        inspectionId: inspection.id,
                                        status: editInspectionStatus,
                                        notes: editInspectionNotes.trim() || undefined,
                                        documentUrl: editInspectionDocUrl.trim() || undefined,
                                      }, {
                                        onSuccess: () => {
                                          setEditingInspectionId(null)
                                        }
                                      })
                                    }}
                                    disabled={updateInspectionStatusMutation.isPending}
                                    className="text-[9px] font-extrabold uppercase tracking-widest text-white bg-[#be001e] hover:bg-[#e6202f] hover:shadow-lg hover:shadow-[#be001e]/20 hover:-translate-y-0.5 px-4 py-1.5 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none cursor-pointer flex items-center gap-1.5"
                                  >
                                    {updateInspectionStatusMutation.isPending ? (
                                      <>
                                        <Loader2 className="size-3 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      "Save Report"
                                    )}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col w-full relative z-10">
                                <div className="flex justify-between items-start gap-3">
                                  <div className="space-y-1 flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <div className="flex items-center justify-center size-6 rounded-md bg-slate-50 border border-slate-100 group-hover:bg-[#be001e]/5 group-hover:border-[#be001e]/20 group-hover:text-[#be001e] text-slate-400 transition-all duration-300">
                                        <ShieldCheck className="size-3.5" />
                                      </div>
                                      <span className="font-extrabold text-slate-800 tracking-tight text-[13px]">Inspection Request</span>
                                      <span className="font-mono text-slate-400 text-[9px] font-bold uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">#{inspection.id.slice(0, 8)}</span>
                                    </div>
                                    {inspection.message && (
                                      <p className="text-[11px] text-slate-600 font-medium leading-relaxed pl-8">
                                        "{inspection.message}"
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                                    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[9px] font-extrabold border uppercase tracking-widest ${
                                      inspection.status === "COMPLETED" || inspection.status === "PASSED" || inspection.status === "APPROVED"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm"
                                        : inspection.status === "PENDING"
                                        ? "bg-amber-50 text-amber-700 border-amber-200 shadow-sm"
                                        : inspection.status === "FAILED" || inspection.status === "REJECTED"
                                        ? "bg-red-50 text-red-700 border-red-200 shadow-sm"
                                        : "bg-slate-50 text-slate-700 border-slate-200 shadow-sm"
                                    }`}>
                                      {inspection.status}
                                    </span>
                                    <span className="text-[9px] text-slate-400 font-bold tracking-wider flex items-center gap-1 uppercase">
                                      <Clock className="size-3" /> {formatDate(inspection.createdAt)}
                                    </span>
                                  </div>
                                </div>

                                {((inspection.notes && inspection.notes !== "string") || (inspection.documentUrl && inspection.documentUrl !== "string")) && (
                                  <div className="mt-3 transition-colors duration-300 relative">
                                    <div className="bg-slate-50/80 border border-slate-100 rounded-lg p-3 space-y-2 hover:bg-white hover:shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300">
                                      <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-[#be001e]">
                                        <FileText className="size-3" />
                                        Official Inspection Report
                                      </div>
                                      {inspection.notes && inspection.notes !== "string" && (
                                        <p className="text-[11px] text-slate-700 font-medium whitespace-pre-line leading-relaxed bg-white/60 rounded-md p-2.5 border border-slate-100/80 shadow-sm">
                                          {inspection.notes}
                                        </p>
                                      )}
                                      {inspection.documentUrl && inspection.documentUrl !== "string" && (
                                        <div className="pt-1">
                                          <a
                                            href={inspection.documentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-700 bg-white border border-slate-200 hover:border-[#be001e] hover:text-[#be001e] px-3 py-1.5 rounded-md transition-all shadow-sm hover:shadow-md group/btn"
                                          >
                                            <Link2 className="size-3 group-hover/btn:scale-110 group-hover/btn:rotate-12 transition-transform" />
                                            View Attached Document
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <div className="flex justify-end pt-3 border-t border-slate-100/80 mt-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingInspectionId(inspection.id)
                                      setEditInspectionStatus(inspection.status)
                                      setEditInspectionNotes(inspection.notes || "")
                                      setEditInspectionDocUrl(inspection.documentUrl || "")
                                    }}
                                    className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-widest text-slate-600 bg-slate-50 hover:bg-[#be001e] border border-slate-200 hover:border-[#be001e] hover:text-white px-3 py-1.5 rounded-md cursor-pointer transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5 group/editbtn"
                                  >
                                    {inspection.notes || inspection.documentUrl ? "Update Report" : "Submit Report"}
                                    <ChevronRight className="size-3 group-hover/editbtn:translate-x-0.5 transition-transform" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-[#be001e]/10 lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-[#e6202f]" /> Timestamps
              </h4>
              <div className="relative pl-5 space-y-4">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-[#be001e]/15" />

                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                    <div className="size-1.5 rounded-full bg-sky-600" />
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">ORDER DATE</span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(order.createdAt)}</p>
                  </div>
                </div>

                {/* {q && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-red-100 bg-[#fee2e2]">
                      <div className="size-1.5 rounded-full bg-red-600" />
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">RFQ REFERENCE</span>
                      <p className="text-xs font-bold text-slate-800 leading-none font-mono">
                        #{q.rfqId?.slice(0, 8).toUpperCase() ?? "N/A"}
                      </p>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedVariantId && (
        <VariantDetailSidebar
          variantId={selectedVariantId}
          onClose={() => setSelectedVariantId(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedProductId && (
        <ProductDetailSidebar
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar 
          company={selectedCompany} 
          onClose={() => setSelectedCompany(null)} 
          level={selectedProductId || selectedVariantId ? 1 : 0}
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar 
          externalSeller={selectedExternalSeller} 
          onClose={() => setSelectedExternalSeller(null)} 
        />
      )}
      {selectedQuotation && (
        <QuotationDetailSidebar
          quotation={selectedQuotation}
          onClose={() => setSelectedQuotation(null)}
        />
      )}
    </div>
    
  )
}

export function OrderDetailsPage({ orderId }: { orderId: string }) {
  const navigate = useNavigate()

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetOrderById(orderId, !!orderId)

  const selectedOrder = detailData?.order || (detailData && !detailData.order && detailData.id ? detailData : null)

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/orders" })
    }
  }

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left min-h-[300px]">
        <div className="absolute top-5 left-5">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Orders"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching order details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Orders"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || "Failed to load order details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedOrder) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Orders"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">Order details not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <OrderDetailView order={selectedOrder} onBack={handleGoBack} />
    </div>
  )
  
}
